import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

import Input from '../../../components/BaseUI/Input'
import Button from '../../../components/BaseUI/Button'
import Content from '../Content'

import styles from './Register.module.scss'
import { useTitle } from '../../../hooks/useTitle'
import { Toast, ToastContainer } from '../../../components/CustomUI/Toast'

const API_URL =
  process.env.REACT_APP_API_URL || 'https://dm.aibuddyassistant.com/api'

export default function Register() {
  const navigate = useNavigate()

  useTitle('Регистрация | AIBuddy CRM')

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  })
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState(null)
  const [errors, setErrors] = useState({})
  const [emailExists, setEmailExists] = useState(false)

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Имя обязательно'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email обязателен'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Некорректный email'
    }

    if (!formData.password) {
      newErrors.password = 'Пароль обязателен'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Пароль должен быть минимум 6 символов'
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Пароли не совпадают'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleRegister = async () => {
    if (!validateForm()) {
      return
    }

    setLoading(true)
    setToast(null)
    setEmailExists(false)

    try {
      const response = await axios.post(`${API_URL}/auth/register`, {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
      })

      // Store token for immediate access
      if (response.data.token) {
        localStorage.setItem('token', response.data.token)
        localStorage.setItem('user', JSON.stringify(response.data.user))
      }

      setToast({
        id: 1,
        title: 'Регистрация успешна!',
        description: response.data.emailSent
          ? 'Проверьте почту для подтверждения email.'
          : 'Добро пожаловать в систему!',
        appearance: 'success',
      })

      // Redirect after short delay
      setTimeout(() => {
        navigate('/dashboard', { replace: true })
      }, 2000)
    } catch (error) {
      console.error('Registration error:', error)
      const errorCode = error.response?.data?.code
      const errorMessage =
        error.response?.data?.error || 'Ошибка регистрации. Попробуйте позже.'

      if (errorCode === 'EMAIL_EXISTS') {
        setEmailExists(true)
      }

      setToast({
        id: 1,
        title: 'Ошибка регистрации',
        description: errorMessage,
        appearance: 'danger',
      })
    } finally {
      setLoading(false)
    }
  }

  const removeToast = () => {
    setToast(null)
  }

  return (
    <div className={styles['register-container']}>
      <Content title="Создайте аккаунт">
        <div className="mt-4">
          <Input
            type="text"
            label="Имя"
            placeholder="Введите ваше имя"
            className={styles['custom__input']}
            value={formData.name}
            onChange={value => {
              setFormData({ ...formData, name: value })
              if (errors.name) setErrors({ ...errors, name: '' })
            }}
            error={errors.name}
          />

          <Input
            type="email"
            label="Email"
            placeholder="Введите ваш email"
            className={styles['custom__input']}
            value={formData.email}
            onChange={value => {
              setFormData({ ...formData, email: value })
              if (errors.email) setErrors({ ...errors, email: '' })
            }}
            error={errors.email}
          />

          <Input
            type="password"
            label="Пароль"
            placeholder="Минимум 6 символов"
            className={styles['custom__input']}
            value={formData.password}
            onChange={value => {
              setFormData({ ...formData, password: value })
              if (errors.password) setErrors({ ...errors, password: '' })
            }}
            error={errors.password}
          />

          <Input
            type="password"
            label="Подтверждение пароля"
            placeholder="Повторите пароль"
            className={styles['custom__input']}
            value={formData.confirmPassword}
            onChange={value => {
              setFormData({ ...formData, confirmPassword: value })
              if (errors.confirmPassword)
                setErrors({ ...errors, confirmPassword: '' })
            }}
            error={errors.confirmPassword}
          />
        </div>

        <div className="mt-2">
          <Button
            title={loading ? 'Регистрация...' : 'Создать аккаунт'}
            appearance="primary"
            isBlock={true}
            onClick={handleRegister}
            disabled={loading}
          />
        </div>

        {emailExists && (
          <div className={styles['email-exists-help']}>
            <p>Этот email уже зарегистрирован. Вы можете:</p>
            <div className={styles['help-links']}>
              <span
                className={styles['create__account']}
                onClick={() => navigate('/login')}
              >
                Войти в аккаунт
              </span>
              <span className={styles['help-separator']}>или</span>
              <span
                className={styles['create__account']}
                onClick={() => navigate('/recover-password')}
              >
                Восстановить пароль
              </span>
            </div>
          </div>
        )}

        <div className={styles['register']}>
          <span>Уже есть аккаунт?</span>
          <span
            className={styles['create__account']}
            onClick={() => navigate('/login')}
          >
            Войти
          </span>
        </div>
      </Content>

      {toast && (
        <ToastContainer onDismissed={removeToast}>
          <Toast {...toast} isExpanded />
        </ToastContainer>
      )}
    </div>
  )
}
